import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import Features from './Features';
import Pricing from './Pricing';
import Reviews from './Review';
import Banner from '../alertpage/Banner';
import BetaDialog from '../alertpage/BetaDialog';
import appFeatures from '../../../assets/appFeatures.png';
import '../../../css/pages.css';
import '../../../css/alertpages.css';

import hawkers1 from '../../../assets/hawkers/hawkers1.png';
import hawkers2 from '../../../assets/hawkers/hawkers2.png';
import hawkers3 from '../../../assets/hawkers/hawkers3.png';
import hawkers4 from '../../../assets/hawkers/hawkers4.png';
import hawkers5 from '../../../assets/hawkers/hawkers5.png';
import hawkers6 from '../../../assets/hawkers/hawkers6.png';
import hawkers7 from '../../../assets/hawkers/hawkers7.png';
import hawkers8 from '../../../assets/hawkers/hawkers8.png';
import hawkers9 from '../../../assets/hawkers/hawkers9.png';
import hawkers10 from '../../../assets/hawkers/hawkers10.png';
import hawkers11 from '../../../assets/hawkers/hawkers11.png';
import hawkers12 from '../../../assets/hawkers/hawkers12.png';

// Motion component aliases to avoid ESLint errors
const MotionDiv = motion.div;
const MotionP = motion.p;
const MotionH1 = motion.h1;
const MotionA = motion.a;
const MotionH6 = motion.h6;
const MotionImg = motion.img;
const MotionSection = motion.section;

const words = [
    "data magic", 
    "speed thrills", 
    "worlds unite", 
    "trends flow", 
    "darkness shines", 
    "beauty wins"
];

const Home = () => {
  const [currentWord, setCurrentWord] = useState("");
  const [isTyping, setIsTyping] = useState(true);
  const [wordIndex, setWordIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const typeEffect = () => {
      const currentFullWord = words[wordIndex];
      
      if (isDeleting) {
        setCurrentWord(currentFullWord.substring(0, charIndex - 1));
        setCharIndex(prev => prev - 1);
      } else {
        setCurrentWord(currentFullWord.substring(0, charIndex + 1));
        setCharIndex(prev => prev + 1);
      }

      // Faster and consistent speeds
      let speed = isDeleting ? 10 : 13; // Made both speeds faster and similar

      if (!isDeleting && charIndex === currentFullWord.length) {
        // Much shorter pause - keep cursor blinking
        speed = 300; // Reduced from 2000ms to 800ms
        setIsTyping(true); // Keep cursor blinking during pause
        setTimeout(() => {
          setIsDeleting(true);
          setIsTyping(false); // Remove typing class when starting to delete
        }, speed);
        return;
      }

      if (isDeleting && charIndex === 0) {
        setIsDeleting(false);
        setWordIndex(prev => (prev + 1) % words.length);
        setIsTyping(true);
        speed = 300; // Reduced pause before starting new word
      }

      timeoutRef.current = setTimeout(typeEffect, speed);
    };

    // Faster initial start
    timeoutRef.current = setTimeout(typeEffect, 200);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [wordIndex, charIndex, isDeleting]);// Added dependencies

  const hawkerImages = [
    hawkers1, hawkers2, hawkers3, hawkers4, hawkers5, hawkers6,
    hawkers7, hawkers8, hawkers9, hawkers10, hawkers11, hawkers12
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const imageVariants = {
    hidden: { scale: 0.9, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <>
      {/* Beta Banner */}
      <Banner />
      
      {/* Beta Dialog Modal */}
      <BetaDialog />

      <MotionDiv 
        className="home-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Floating Elements */}
        <div className="floating-elements">
          <MotionDiv 
            className="floating-element"
            animate={{
              y: [-20, 20, -20],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          <MotionDiv 
            className="floating-element"
            animate={{
              y: [20, -20, 20],
              rotate: [360, 180, 0],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          <MotionDiv 
            className="floating-element"
            animate={{
              y: [-10, 30, -10],
              rotate: [0, -180, -360],
            }}
            transition={{
              duration: 30,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        </div>

        {/* Hero Section */}
        <MotionDiv className="hero-section">
          <MotionP 
            className="hero-subtitle"
            variants={itemVariants}
          >
            REVOLUTIONARY POS INNOVATION
          </MotionP>
          
          <MotionH1 
            className="hero-title"
            variants={itemVariants}
          >
            Where{' '}
            <span className="gradient-text glitch">genius meets</span>
            <br />
            <span className={`typing-text ${isTyping ? 'typing' : ''} ${isDeleting ? 'deleting' : ''}`}>
              {currentWord}
            </span>
          </MotionH1>

          <MotionDiv 
            className="home-content"
            variants={containerVariants}
          >
            {/* Left Column */}
            <MotionDiv 
              className="left-column"
              variants={itemVariants}
            >
              <h2 className="gradient-text">
                Revolutionize your hawker stall with cutting-edge AI technology. 
                Skyrocket sales, automate operations, and serve customers lightning-fast.
              </h2>
              <p>
                Experience the future of food service with our intelligent POS ecosystem. 
                Advanced AI algorithms learn your business patterns, predict customer preferences, 
                and optimize every transaction for maximum profitability.
              </p>
              <MotionDiv 
                className="cta-links"
                variants={itemVariants}
              >
                <MotionA 
                  className="btn btn-gradient start-btn" 
                  href="/getstarted"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Launch Free Trial
                </MotionA>
                <MotionA 
                  className="btn btn-outline request-demo-home-btn" 
                  href="/request-demo"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Book Live Demo
                </MotionA>
              </MotionDiv>
            </MotionDiv>

            {/* Right Column */}
            <MotionDiv 
              className="right-column"
              variants={imageVariants}
            >
              <MotionDiv 
                className="app-features-container"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src={appFeatures} 
                  alt="POSlah Revolutionary Features" 
                  className="app-features"
                  loading="lazy"
                />
                <div className="image-glow"></div>
              </MotionDiv>
            </MotionDiv>
          </MotionDiv>
        </MotionDiv>

        {/* Trusted Section */}
        <MotionDiv 
          className="trusted-section"
          variants={itemVariants}
        >
          <MotionH6 
            className="trusted-text"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Empowering Singapore's Most Innovative Hawker Entrepreneurs
          </MotionH6>

          <div className="trusted-banner-wrapper">
            <MotionDiv 
              className="trusted-banner"
              animate={{ x: ['0%', '-50%'] }}
              transition={{
                duration: 25,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              {/* Original images with alternating gradients */}
              {hawkerImages.map((hawker, index) => (
                <MotionImg
                  key={`original-${index}`}
                  src={hawker}
                  alt={`innovative hawker ${index + 1}`}
                  className={`trusted-icon ${index % 2 === 0 ? 'gradient-1' : 'gradient-2'}`}
                  loading="lazy"
                  whileHover={{ 
                    scale: 1.1, 
                    rotate: 5,
                    filter: 'grayscale(0%) opacity(1)'
                  }}
                  transition={{ duration: 0.3 }}
                />
              ))}
              
              {/* Duplicate images for seamless loop */}
              {hawkerImages.map((hawker, index) => (
                <MotionImg
                  key={`duplicate-${index}`}
                  src={hawker}
                  alt={`innovative hawker ${index + 1}`}
                  className={`trusted-icon ${index % 2 === 0 ? 'gradient-1' : 'gradient-2'}`}
                  loading="lazy"
                  whileHover={{ 
                    scale: 1.1, 
                    rotate: 5,
                    filter: 'grayscale(0%) opacity(1)'
                  }}
                  transition={{ duration: 0.3 }}
                />
              ))}
            </MotionDiv>
          </div>
        </MotionDiv>
      </MotionDiv>

      {/* Features Section */}
      <MotionSection 
        id="features"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <Features />
      </MotionSection>

      {/* Pricing Section */}
      <MotionSection 
        id="pricing"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <Pricing />
      </MotionSection>

      {/* Reviews Section */}
      <MotionSection 
        id="reviews"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <Reviews />
      </MotionSection>
    </>
  );
};

export default Home;